import React, { useEffect } from 'react';
import { Leave } from '../../../../../data-models';
import {
  HighlightNote,
  Text,
  SingleBorderContainer,
} from '../../../tw-components';
import dayjs from 'dayjs';
import { dateFormat } from './shared-styles';
import {
  getPtoChoice,
  putPtoChoice,
} from '../../../../../utils/web-apis-client/index';
import EditIcon from '../../../../../images/blue-pen.svg';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import {
  RadioButton,
  RadioButtonChangeEvent,
} from '@progress/kendo-react-inputs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '../../../spinner';
import { HelpIcon } from '../../../claim-card/components/tw-components';

const PostIntakeLeaves: (props: {
  leaves: Array<Leave>;
  absenceNotifications?: any;
  ptoChoice?: string;
  statusTracker?: string;
}) => React.ReactElement = (props) => {
  const leaveId = props.leaves?.[0]?.leaveId;
  const leaveType = props.leaves?.[0]?.absenceType;
  const [visible, setVisible] = React.useState<boolean>(false);
  const toggleDialog = () => {
    setVisible(!visible);
  };
  const [selectedPtoOption, setSelectedPtoOption] = React.useState('');
  const handlePtoOptionChange = React.useCallback(
    (e: RadioButtonChangeEvent) => {
      setSelectedPtoOption(e.value);
    },
    [setSelectedPtoOption],
  );

  const getNtn = (leaveId: string | undefined) => {
    if (leaveType === 'Continuous') {
      const parentNtn = leaveId?.match(/^([^-]+-[^-]+)/)?.[1];
      const parentAbsenceNotification =
        props.absenceNotifications?.absenceNotifications?.find(
          (notification: any) => notification.notificationCaseId === parentNtn,
        );
      const claims = parentAbsenceNotification?.claims || [];
      const claimId = claims?.[0]?.claimId;
      return claimId ?? leaveId;
    }

    return leaveId;
  };

  const ntn = getNtn(leaveId);
  const { data: currentPtoChoice } = useQuery({
    queryKey: ['currentPtoChoice', ntn],
    queryFn: () => getPtoChoice('', ntn),
    enabled: !!ntn,
  });

  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = React.useState(false);
  const [showEditPto, setShowEditPto] = React.useState(true);
  useEffect(() => {
    for (const leave of props.leaves) {
      if (
        props?.ptoChoice === 'Intermittent Actual Only' &&
        props.statusTracker !== 'Closed'
      ) {
        setShowEditPto(true);
      } else if (leave.requestStatus !== 'Pending') {
        setShowEditPto(false);
        break;
      }
    }
  }, [props.leaves, props.ptoChoice, props.statusTracker]);

  const toolTipHelpText = (
    <div>
      <p>
        Your employer allows you to use the paid time off you have available to
        cover your elimination period.
      </p>
      <ul>
        <li> We will share your updated choice with your employer. </li>
        <li>
          Your employer will review your available paid time off to determine
          how much can be applied.
        </li>
        <li>
          Please reach out to your employer if you have questions about your
          paid time off balances.
        </li>
      </ul>
      <p>
        * An elimination period refers to the amount of time you must be
        appproved for benefits before they become payable.
      </p>
    </div>
  );

  return (
    <>
      <div className="tw-mb-5">
        <HighlightNote>
          We are currently evaluating your request and all applicable coverages
          for your case and will provide an update to you. Please allow up to
          five business days for an update.
        </HighlightNote>
      </div>

      <div className="tw-mb-5">
        <SingleBorderContainer>
          <Text
            className="tw-w-full tw-mb-6"
            align="center"
            color="unm-interactive01-400"
          >
            Summary of requested leave
          </Text>
          {props.leaves?.map((leave) => (
            <div key={leave.leaveId}>
              <div className="tw-mb-6 tw-flex tw-flex-col sm:tw-flex-row tw-items-center sm:tw-justify-around">
                <div className="tw-flex tw-items-center tw-pb-2 sm:tw-pb-0">
                  <Text align="center" fontWeight="semibold">
                    Requested dates:&nbsp;{' '}
                  </Text>
                  <Text align="center">
                    {dayjs(leave.startDate).format(dateFormat)} -{' '}
                    {dayjs(leave.endDate).format(dateFormat)}
                  </Text>
                </div>
                <div className="tw-flex tw-items-center">
                  <Text align="center" fontWeight="semibold">
                    Leave Schedule:&nbsp;{' '}
                  </Text>
                  <Text align="center">{leave.absenceType}</Text>
                </div>
              </div>
            </div>
          ))}
          <Text className="tw-w-full" align="center">
            Please note, requested dates may be adjusted based on information
            received from you or your health care provider. As a Unum standard,
            intermittent leave requests are defaulted to six months from your
            requested leave start date.
          </Text>
          {props?.ptoChoice === 'Intermittent Actual and Elimination Period' ||
          props?.ptoChoice === 'Intermittent Actual Only' ||
          props?.ptoChoice === 'Elimination Period Only' ? (
            <div className="tw-mt-6 tw-flex tw-flex-row  tw-content-center tw-border-solid tw-border-t tw-border-b-0 tw-border-x-0 tw-border-t-unm-adhoc2 tw-pt-[20px] tw-pb-[12px]">
              <div className="tw-flex">
                <Text className="tw-text-left tw-font-sans tw-font-semibold tw-text-base">
                  Include paid time off
                </Text>
                <div className="tw-p-2 tw-pt-1">
                  <HelpIcon popupContent={toolTipHelpText} />
                </div>
                <Text className="tw-text-left tw-font-sans tw-font-semibold tw-text-base">
                  :
                </Text>
                <Text className={'tw-flex tw-p-1 tw-pt-0'}>
                  {currentPtoChoice}
                </Text>
              </div>
              {showEditPto === true ? (
                <div className="tw-flex">
                  <Button
                    type={'button'}
                    className="tw-ml-4 tw-p-1 tw-pt-0 tw-bg-transparent tw-border-0"
                    onClick={toggleDialog}
                  >
                    <img src={EditIcon} alt="edit" className="tw-inline" />
                    <Text className="tw-inline tw-text-unm-mixed tw-font-sans tw-font-normal tw-text-base">
                      Edit
                    </Text>
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          {visible && (
            <Dialog className={''}>
              <div
                className={
                  'tw-flex tw-w-full tw-justify-between tw-items-center'
                }
              >
                <div>
                  <text
                    className={
                      'tw-font-sans tw-font-normal tw-text-2xl tw-text-unm-adhoc2'
                    }
                  >
                    Update Paid Time Off Selection
                  </text>
                </div>
                <div>
                  <Button
                    onClick={() => {
                      toggleDialog();
                    }}
                    className="tw-bg-white tw-border-transparent tw-pr-0 tw-pt-0 tw-text-[#015294]"
                  >
                    X Close
                  </Button>
                </div>
              </div>
              <div className="tw-p-5">
                {isSaving ? (
                  <div className="tw-flex tw-justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <Text className="tw-mb-4">
                      Would you like to use your paid time off during your
                      intermittent absence(s)?
                    </Text>
                    <Text className="tw-mb-4">
                      Your previous selection will be updated for the remainder
                      of the leave.
                    </Text>
                    <Text className="tw-mb-4">
                      <ul>
                        <li>
                          Selecting yes applies paid time off for future
                          intermittent absences.
                        </li>
                        <li>
                          Selecting no will not apply paid time off for future
                          intermittent absences.
                        </li>
                      </ul>
                    </Text>
                    <Text className="tw-mb-4">
                      After saving, you may need to refresh the page to view
                      your updated selection.
                    </Text>
                    <Text className="tw-mb-4">
                      Of note: Your employer's time off and leave policies may
                      affect how available time off can be used during your
                      leave. This may include rules related to eligibility, time
                      off accruals, and available balances. Please reach out to
                      your employer if you have questions.{' '}
                    </Text>
                    <RadioButton
                      name={'Yes'}
                      value={'Yes'}
                      label={'Yes'}
                      onChange={handlePtoOptionChange}
                      checked={selectedPtoOption === 'Yes'}
                    />
                    <RadioButton
                      name={'No'}
                      value={'No'}
                      label={'No'}
                      onChange={handlePtoOptionChange}
                      checked={selectedPtoOption === 'No'}
                    />
                  </>
                )}
              </div>
              <div
                className={
                  'tw-flex tw-w-full tw-justify-between tw-items-center'
                }
              >
                <div></div>
                <div className={'tw-flex'}>
                  <Button
                    onClick={() => {
                      toggleDialog();
                    }}
                    className="tw-mr-4 tw-bg-white tw-border-2 tw-border-[#015294] tw-text-[#015294]"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={isSaving}
                    onClick={async () => {
                      setIsSaving(true);
                      try {
                        await putPtoChoice(getNtn(leaveId), selectedPtoOption);
                        queryClient.invalidateQueries({
                          queryKey: ['currentPtoChoice', leaveId],
                        });
                      } finally {
                        setIsSaving(false);
                        toggleDialog();
                      }
                    }}
                    className={'tw-bg-[#015294] tw-text-white'}
                  >
                    Save / Update
                  </Button>
                </div>
              </div>
            </Dialog>
          )}
        </SingleBorderContainer>
      </div>
    </>
  );
};

export default PostIntakeLeaves;
